import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Dialog,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable, CreateCategoryForm } from "../../components";
import { baseAxios } from "../../utils/config";
import { colors } from "..";

const FLAG_OPTIONS = ["Chemical", "Non Chemical"];

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : colors.white,
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: colors.white,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function categoryMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [row.category_name, row.flag]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareCategoryRows(a, b, field) {
  let primary = 0;
  const va = a[field];
  const vb = b[field];
  const sa = va == null ? "" : String(va);
  const sb = vb == null ? "" : String(vb);
  primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  if (primary !== 0) return primary;
  return (Number(a.category_id) || 0) - (Number(b.category_id) || 0);
}

export default function ManageCategories() {
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [dialogCategoryId, setDialogCategoryId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterFlag, setFilterFlag] = useState("");
  const [flagFilterMenuAnchor, setFlagFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("category_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const openCreateCategory = useCallback(() => {
    setDialogCategoryId(null);
    setCategoryDialogOpen(true);
  }, []);

  const openEditCategory = useCallback((categoryId) => {
    setDialogCategoryId(categoryId);
    setCategoryDialogOpen(true);
  }, []);

  const closeCategoryDialog = useCallback(() => {
    setCategoryDialogOpen(false);
  }, []);

  const handleCategoryDialogExited = useCallback(() => {
    setDialogCategoryId(null);
  }, []);

  const loadCategories = useCallback(() => {
    setLoading(true);
    baseAxios
      .get("/categories")
      .then((res) => {
        setCategories(res?.data?.categories ?? []);
      })
      .catch(() => {
        setCategories([]);
        toast.error("Could not load categories.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, []);

  const onCategorySaved = useCallback(() => {
    loadCategories();
    closeCategoryDialog();
  }, [loadCategories, closeCategoryDialog]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = useMemo(() => {
    return categories.filter((row) => {
      if (!categoryMatchesSearch(row, deferredSearch)) return false;
      if (filterFlag && row.flag !== filterFlag) return false;
      return true;
    });
  }, [categories, deferredSearch, filterFlag]);

  const sortedCategories = useMemo(() => {
    const copy = [...filteredCategories];
    copy.sort((a, b) => {
      const c = compareCategoryRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredCategories, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "category_name",
        label: "Category Name",
        field: "category_name",
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "flag",
        label: "Flag",
        field: "flag",
        minWidth: 160,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by flag"
            value={filterFlag}
            onChange={setFilterFlag}
            anchorEl={flagFilterMenuAnchor}
            setAnchorEl={setFlagFilterMenuAnchor}
            allMenuLabel="All flags"
            options={FLAG_OPTIONS.map((flag) => ({
              value: flag,
              label: flag,
            }))}
          />
        ),
        render: (value) => {
          if (!value) return "-";
          return (
            <Chip
              size="small"
              variant="outlined"
              label={<HighlightText text={value} query={sq} />}
              sx={{ fontWeight: 500, lineHeight: 1 }}
            />
          );
        },
      },
      {
        id: "color",
        label: "Color",
        field: "color",
        sortable: false,
        minWidth: 80,
        render: (value) =>
          value ? (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                backgroundColor: value,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          ) : (
            "-"
          ),
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEditCategory(row.category_id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Edit
          </MuiButton>
        ),
      },
    ];
  }, [deferredSearch, filterFlag, flagFilterMenuAnchor, openEditCategory]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, search, and edit service categories, flags, and colors.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search category name or flag…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh categories"
            onClick={loadCategories}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openCreateCategory}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add Category
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedCategories}
        totalRowCount={sortedCategories.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="category_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterFlag}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <Dialog
        open={categoryDialogOpen}
        onClose={closeCategoryDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        TransitionProps={{ onExited: handleCategoryDialogExited }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <CreateCategoryForm
          key={
            dialogCategoryId == null
              ? "new-category"
              : `category-${dialogCategoryId}`
          }
          categoryId={dialogCategoryId}
          onSaved={onCategorySaved}
          onRequestClose={closeCategoryDialog}
        />
      </Dialog>
    </Box>
  );
}
